"use client";

import { useId } from "react";
import { useGardenAtmosphere } from "./GardenAtmosphereProvider";
import { gardenPalette, isGardenDay } from "@/lib/garden-atmosphere";

const STARS = [
  [470, 38, 3],
  [560, 74, 4],
  [670, 27, 3],
  [780, 52, 5],
  [880, 25, 3],
  [935, 93, 4],
  [730, 106, 2],
];

export default function GardenScene() {
  const { sceneMinutes: minutes, season } = useGardenAtmosphere();
  const gradient = useId();
  const palette = gardenPalette(minutes, season);
  const day = isGardenDay(minutes);
  const daylight = Math.max(
    0,
    Math.sin((((minutes % 1440) - 360) / 720) * Math.PI),
  );
  const orbit = day
    ? (minutes - 390) / 720
    : ((minutes - 1110 + 1440) % 1440) / 720;

  return (
    <div
      className="garden-scene"
      data-season={season}
      data-day={day}
      aria-hidden="true"
    >
      <div
        className="garden-celestial-position"
        style={{
          left: `${60 + orbit * 28}%`,
          top: `${12 + (1 - Math.sin(orbit * Math.PI)) * 18}%`,
        }}
      >
        <div className="garden-celestial">
          <svg viewBox="-50 -50 100 100" width="100" height="100">
            {day ? (
              <>
                <circle r="46" fill="#fff5cb" opacity=".16" />
                <circle r="34" fill="#fff5cb" opacity=".22" />
                <circle r="23" fill="#f2d994" />
              </>
            ) : (
              <>
                <circle r="33" fill="#e8eeff" opacity=".06" />
                <path
                  d="M10 -23 A25 25 0 1 0 22 15 A23 23 0 0 1 10 -23"
                  fill="#d6e0f4"
                />
              </>
            )}
          </svg>
        </div>
      </div>
      <svg
        className="garden-starfield"
        viewBox="400 0 600 140"
        style={{ opacity: 1 - daylight }}
      >
        <g fill="#eef4ff">
          {STARS.map(([sx, sy, r]) => (
            <path
              key={sx}
              d={`M${sx} ${sy - r * 2} Q${sx} ${sy} ${sx + r} ${sy} Q${sx} ${sy} ${sx} ${sy + r * 2} Q${sx} ${sy} ${sx - r} ${sy} Q${sx} ${sy} ${sx} ${sy - r * 2}`}
            />
          ))}
          <path
            d="M560 74 L670 27 L780 52"
            stroke="#dce8ff"
            strokeWidth=".6"
            fill="none"
            opacity=".3"
          />
        </g>
      </svg>
      <svg
        aria-hidden="true"
        className="garden-landscape"
        viewBox="0 0 1000 240"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
            <stop stopColor={palette.foliage} stopOpacity=".35" />
            <stop offset="1" stopColor={palette.hill} />
          </linearGradient>
        </defs>
        <g opacity={day ? ".45" : ".12"} fill="#fff">
          <path d="M580 127 Q600 115 632 123 Q650 102 678 123 Q708 114 730 131 Z" />
          <path d="M840 109 Q866 94 884 107 Q901 92 923 111 Z" />
        </g>
        <path
          d="M0 194 Q140 142 290 185 T610 175 T1000 163 V240 H0Z"
          fill={palette.foliage}
          opacity={day ? ".42" : ".15"}
        />
        <path
          d="M0 219 Q220 179 460 213 T770 207 T1000 195 V240 H0Z"
          fill={`url(#${gradient})`}
          opacity={day ? ".7" : ".4"}
        />
        <path
          d="M0 236 Q245 209 475 230 T1000 217 V240 H0Z"
          fill={palette.hill}
          opacity=".55"
        />
        <g
          stroke={day ? "#586b63" : "#71828d"}
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
        >
          <path d="M914 229 V127 M914 190 L882 158 M914 174 L944 145 M914 157 L898 137" />
          <path
            d="M849 232 V167 M849 207 L828 187 M849 198 L871 180"
            strokeWidth="2"
          />
        </g>
        {season === "winter" ? (
          <g stroke="#eaf4fa" strokeWidth="3" strokeLinecap="round" fill="none">
            <path d="M882 155 L910 183 M919 170 L944 143 M900 134 L914 147 M829 185 L843 199" />
            <path d="M784 231 Q836 219 876 228 M894 228 Q919 221 942 229" />
          </g>
        ) : (
          <g fill={palette.foliage} opacity={day ? ".95" : ".6"}>
            <ellipse
              cx="900"
              cy="145"
              rx="26"
              ry="34"
              transform="rotate(-30 900 145)"
            />
            <ellipse
              cx="939"
              cy="153"
              rx="27"
              ry="35"
              transform="rotate(25 939 153)"
            />
            <ellipse cx="918" cy="126" rx="26" ry="34" />
            <ellipse cx="844" cy="180" rx="21" ry="27" />
            <ellipse cx="867" cy="183" rx="17" ry="24" />
          </g>
        )}
        {season === "spring" ? (
          <g fill="#f5d8e8">
            {[
              [895, 137],
              [924, 113],
              [942, 150],
              [837, 173],
              [863, 185],
            ].map(([px, py]) => (
              <g key={px} transform={`translate(${px} ${py})`}>
                <circle cx="-3" r="3" />
                <circle cx="3" r="3" />
                <circle cy="-3" r="3" />
                <circle cy="3" r="3" />
              </g>
            ))}
          </g>
        ) : null}
        {season === "autumn" ? (
          <g fill="#dcb083">
            <ellipse
              cx="883"
              cy="206"
              rx="4"
              ry="7"
              transform="rotate(35 883 206)"
            />
            <ellipse
              cx="961"
              cy="191"
              rx="4"
              ry="7"
              transform="rotate(-30 961 191)"
            />
            <ellipse cx="930" cy="232" rx="7" ry="3" />
          </g>
        ) : null}
        {season === "summer" ? (
          <g fill="#f6df87">
            <circle cx="794" cy="225" r="3" />
            <circle cx="805" cy="220" r="2" />
            <circle cx="775" cy="229" r="2" />
          </g>
        ) : null}
        {season === "winter" ? (
          <g fill="#eaf4fa" opacity=".75">
            {[
              [620, 160],
              [795, 146],
              [880, 87],
              [958, 134],
              [740, 198],
            ].map(([px, py]) => (
              <path
                key={px}
                d={`M${px - 3} ${py}h6 M${px} ${py - 3}v6 M${px - 2} ${py - 2}l4 4 M${px + 2} ${py - 2}l-4 4`}
                stroke="#eaf4fa"
                strokeWidth="1"
              />
            ))}
          </g>
        ) : null}
      </svg>
    </div>
  );
}
